import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Mail, CheckCircle2, AlertTriangle, XCircle, 
  ChevronRight, Loader2, X, Search, User,
  Send, Eye, MousePointer, AlertOctagon,
  TrendingUp, TrendingDown, Minus
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status colors and icons - DARK THEME
const statusConfig = {
  saudavel: {
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    icon: CheckCircle2,
    label: 'Saudável'
  },
  atencao: {
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    icon: AlertTriangle,
    label: 'Atenção'
  },
  problema: {
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30',
    icon: XCircle,
    label: 'Problema'
  },
  neutro: {
    color: 'text-stone-400',
    bgColor: 'bg-stone-500/10',
    borderColor: 'border-stone-500/30',
    icon: Minus,
    label: 'Sem dados'
  }
};

// Indicator component - DARK THEME
function Indicator({ label, value }) {
  const getIndicatorStyle = (val) => {
    if (val === 'OK') return 'text-green-400 bg-green-500/20';
    if (val === 'Médio') return 'text-amber-400 bg-amber-500/20';
    if (val === 'Baixo') return 'text-red-400 bg-red-500/20';
    return 'text-stone-400 bg-stone-500/20';
  };

  return (
    <div className="flex items-center justify-between">
      <span className="text-stone-400 text-sm">{label}</span>
      <span className={`text-xs font-medium px-2 py-0.5 rounded ${getIndicatorStyle(value)}`}>
        {value}
      </span>
    </div>
  );
}

// Event badge - DARK THEME
function EventBadge({ type }) {
  const config = {
    delivered: { color: 'bg-blue-500/20 text-blue-400', label: 'Entregue' },
    opened: { color: 'bg-green-500/20 text-green-400', label: 'Aberto' },
    clicked: { color: 'bg-purple-500/20 text-purple-400', label: 'Clicado' },
    bounced: { color: 'bg-red-500/20 text-red-400', label: 'Rejeitado' }
  };
  const { color, label } = config[type] || { color: 'bg-stone-500/20 text-stone-400', label: type };
  
  return (
    <span className={`text-xs px-2 py-0.5 rounded ${color}`}>{label}</span>
  );
}

export default function EmailHealthCard({ period = '30d' }) {
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [showSheet, setShowSheet] = useState(false);
  const [sheetMode, setSheetMode] = useState('macro'); // 'macro' | 'user'
  
  // User investigation state
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);

  useEffect(() => {
    fetchEmailHealth();
  }, [period]);

  const fetchEmailHealth = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/email-health/health`, {
        params: { period },
        withCredentials: true
      });
      setHealth(response.data);
    } catch (error) {
      console.error('Failed to fetch email health:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      setUsersLoading(true);
      const response = await axios.get(`${API_URL}/api/email-health/users`, {
        params: { search: searchQuery, period },
        withCredentials: true
      });
      setUsers(response.data.users || []);
      setSheetMode('user');
    } catch (error) {
      console.error('Failed to search users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchUserDetail = async (userId) => {
    try {
      setUserDetailLoading(true);
      setSelectedUser(userId);
      const response = await axios.get(`${API_URL}/api/email-health/users/${userId}`, {
        params: { period },
        withCredentials: true
      });
      setUserDetail(response.data);
    } catch (error) {
      console.error('Failed to fetch user detail:', error);
    } finally {
      setUserDetailLoading(false);
    }
  };

  const openMacroSheet = () => {
    setSheetMode('macro');
    setSelectedUser(null);
    setUserDetail(null);
    setShowSheet(true);
  };

  const openUserSheet = () => {
    setSheetMode('user');
    setUsers([]);
    setSearchQuery('');
    setSelectedUser(null);
    setUserDetail(null);
    setShowSheet(true);
  };

  if (loading) {
    return (
      <div className="bg-stone-900 rounded-xl p-6 border border-stone-700/50 animate-pulse">
        <div className="h-6 bg-stone-700 rounded w-1/3 mb-4" />
        <div className="h-4 bg-stone-700 rounded w-2/3" />
      </div>
    );
  }

  if (!health) {
    return (
      <div className="bg-stone-900 rounded-xl p-6 border border-stone-700/50">
        <div className="flex items-center gap-3 text-stone-500">
          <Mail className="w-5 h-5" />
          <span>Dados de email não disponíveis</span>
        </div>
      </div>
    );
  }

  const status = statusConfig[health.status] || statusConfig.neutro;
  const StatusIcon = status.icon;

  return (
    <>
      {/* Main Card - Clicável - DARK THEME */}
      <div
        onClick={openMacroSheet}
        className={`bg-stone-900 rounded-xl p-6 border ${status.borderColor} cursor-pointer 
                    hover:border-stone-600 transition-all duration-200 group`}
        data-testid="email-health-card"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${status.bgColor}`}>
              <Mail className={`w-5 h-5 ${status.color}`} />
            </div>
            <div>
              <h3 className="font-medium text-stone-100">Comunicação por E-mail</h3>
              <div className="flex items-center gap-2 mt-1">
                <StatusIcon className={`w-4 h-4 ${status.color}`} />
                <span className={`text-sm font-medium ${status.color}`}>{status.label}</span>
              </div>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-stone-500 group-hover:text-stone-300 transition-colors" />
        </div>

        {/* Resumo curto */}
        <p className="text-sm text-stone-400 mb-4">{health.resumo}</p>

        {/* Indicadores simplificados */}
        <div className="space-y-2">
          <Indicator label="Entrega" value={health.indicators?.delivery} />
          <Indicator label="Abertura" value={health.indicators?.open} />
          <Indicator label="Cliques" value={health.indicators?.click} />
        </div>
      </div>

      {/* Sheet/Lateral - DARK THEME */}
      <Sheet open={showSheet} onOpenChange={setShowSheet}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto bg-stone-900 border-stone-700">
          <SheetHeader className="mb-6">
            <SheetTitle className="flex items-center gap-2 text-stone-100">
              <Mail className="w-5 h-5 text-amber-500" />
              Comunicação por E-mail
            </SheetTitle>
          </SheetHeader>

          {/* Search bar no topo - DARK */}
          <div className="mb-6">
            <div className="flex gap-2">
              <Input
                placeholder="Buscar por nome ou email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
                className="flex-1 bg-stone-800 border-stone-700 text-stone-100 placeholder:text-stone-500"
              />
              <Button 
                onClick={searchUsers} 
                disabled={usersLoading}
                variant="outline"
                size="icon"
                className="border-stone-700 hover:bg-stone-800 text-stone-300"
              >
                {usersLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Modo Macro (validação numérica) - DARK THEME */}
          {sheetMode === 'macro' && !selectedUser && (
            <div className="space-y-6">
              {/* Status banner */}
              <div className={`p-4 rounded-lg ${status.bgColor} border ${status.borderColor}`}>
                <div className="flex items-center gap-2 mb-2">
                  <StatusIcon className={`w-5 h-5 ${status.color}`} />
                  <span className={`font-medium ${status.color}`}>{status.label}</span>
                </div>
                <p className="text-sm text-stone-300">{health.resumo}</p>
              </div>

              {/* Totais numéricos - DARK */}
              <div>
                <h4 className="text-sm font-medium text-stone-200 mb-3">Totais do Período</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-stone-800 rounded-lg p-3 border border-stone-700/50">
                    <div className="flex items-center gap-2 mb-1">
                      <Send className="w-4 h-4 text-stone-400" />
                      <span className="text-xs text-stone-400">Enviados</span>
                    </div>
                    <span className="text-xl font-semibold text-stone-100">
                      {health.totals?.sent?.toLocaleString() || 0}
                    </span>
                  </div>
                  <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/30">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle2 className="w-4 h-4 text-blue-400" />
                      <span className="text-xs text-stone-400">Entregues</span>
                    </div>
                    <span className="text-xl font-semibold text-blue-400">
                      {health.totals?.delivered?.toLocaleString() || 0}
                    </span>
                    {health.rates?.delivery_rate && (
                      <span className="text-xs text-blue-400/70 ml-1">({health.rates.delivery_rate}%)</span>
                    )}
                  </div>
                  <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/30">
                    <div className="flex items-center gap-2 mb-1">
                      <Eye className="w-4 h-4 text-green-400" />
                      <span className="text-xs text-stone-400">Abertos</span>
                    </div>
                    <span className="text-xl font-semibold text-green-400">
                      {health.totals?.opened?.toLocaleString() || 0}
                    </span>
                    {health.rates?.open_rate && (
                      <span className="text-xs text-green-400/70 ml-1">({health.rates.open_rate}%)</span>
                    )}
                  </div>
                  <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/30">
                    <div className="flex items-center gap-2 mb-1">
                      <MousePointer className="w-4 h-4 text-purple-400" />
                      <span className="text-xs text-stone-400">Cliques</span>
                    </div>
                    <span className="text-xl font-semibold text-purple-400">
                      {health.totals?.clicked?.toLocaleString() || 0}
                    </span>
                    {health.rates?.click_rate && (
                      <span className="text-xs text-purple-400/70 ml-1">({health.rates.click_rate}%)</span>
                    )}
                  </div>
                  <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/30 col-span-2">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertOctagon className="w-4 h-4 text-red-400" />
                      <span className="text-xs text-stone-400">Bounces (Rejeitados)</span>
                    </div>
                    <span className="text-xl font-semibold text-red-400">
                      {health.totals?.bounced?.toLocaleString() || 0}
                    </span>
                    {health.rates?.bounce_rate && (
                      <span className="text-xs text-red-400/70 ml-1">({health.rates.bounce_rate}%)</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Diagnóstico automático - DARK */}
              <div>
                <h4 className="text-sm font-medium text-stone-200 mb-2">Diagnóstico</h4>
                <p className="text-sm text-stone-300 bg-stone-800 p-3 rounded-lg border border-stone-700/50">
                  {health.diagnosis}
                </p>
              </div>

              {/* Ações sugeridas - DARK */}
              {health.actions && health.actions.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-stone-200 mb-2">Ações Sugeridas</h4>
                  <ul className="space-y-2">
                    {health.actions.map((action, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-stone-300">
                        <ChevronRight className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Link para investigar usuários - DARK */}
              <Button
                variant="outline"
                className="w-full border-stone-700 hover:bg-stone-800 text-stone-200"
                onClick={() => {
                  setSheetMode('user');
                }}
              >
                <User className="w-4 h-4 mr-2" />
                Investigar usuários
                <ChevronRight className="w-4 h-4 ml-auto" />
              </Button>
            </div>
          )}

          {/* Modo User (investigação) - Lista de usuários - DARK THEME */}
          {sheetMode === 'user' && !selectedUser && (
            <div className="space-y-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSheetMode('macro')}
                className="text-stone-400 hover:text-stone-200 hover:bg-stone-800"
              >
                ← Voltar para visão geral
              </Button>

              {users.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Busque por nome ou email para investigar</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {users.map((user) => {
                    const userStatus = statusConfig[user.status] || statusConfig.neutro;
                    return (
                      <div
                        key={user.user_id}
                        onClick={() => fetchUserDetail(user.user_id)}
                        className="p-3 rounded-lg border border-stone-700/50 bg-stone-800/50 cursor-pointer hover:border-amber-500/50 hover:bg-stone-800 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-stone-100">{user.name || 'Sem nome'}</p>
                            <p className="text-sm text-stone-400">{user.email}</p>
                          </div>
                          <div className={`px-2 py-1 rounded text-xs font-medium ${userStatus.bgColor} ${userStatus.color}`}>
                            {userStatus.label}
                          </div>
                        </div>
                        <div className="flex gap-4 mt-2 text-xs text-stone-500">
                          <span>Enviados: {user.total_sent}</span>
                          <span>Entregues: {user.total_delivered}</span>
                          <span>Abertos: {user.total_opened}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Detalhe do usuário selecionado - DARK THEME */}
          {selectedUser && (
            <div className="space-y-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedUser(null);
                  setUserDetail(null);
                }}
                className="text-stone-400 hover:text-stone-200 hover:bg-stone-800"
              >
                ← Voltar para lista
              </Button>

              {userDetailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                </div>
              ) : userDetail ? (
                <div className="space-y-6">
                  {/* User header */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-stone-800 border border-stone-700 flex items-center justify-center">
                      <User className="w-5 h-5 text-stone-400" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-100">{userDetail.user?.name || 'Sem nome'}</p>
                      <p className="text-sm text-stone-400">{userDetail.user?.email}</p>
                    </div>
                  </div>

                  {/* Diagnóstico (primeiro) */}
                  <div className={`p-4 rounded-lg ${statusConfig[userDetail.status]?.bgColor || 'bg-stone-800'} border ${statusConfig[userDetail.status]?.borderColor || 'border-stone-700'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {React.createElement(statusConfig[userDetail.status]?.icon || Minus, {
                        className: `w-5 h-5 ${statusConfig[userDetail.status]?.color || 'text-stone-400'}`
                      })}
                      <span className={`font-medium ${statusConfig[userDetail.status]?.color || 'text-stone-400'}`}>
                        {statusConfig[userDetail.status]?.label || 'Neutro'}
                      </span>
                    </div>
                    <p className="text-sm text-stone-300">{userDetail.diagnosis}</p>
                  </div>

                  {/* Ação sugerida (segundo) */}
                  {userDetail.action && (
                    <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <ChevronRight className="w-4 h-4 text-amber-400 mt-0.5" />
                      <span className="text-sm text-amber-200">{userDetail.action}</span>
                    </div>
                  )}

                  {/* Histórico de eventos (terceiro) */}
                  <div>
                    <h4 className="text-sm font-medium text-stone-200 mb-3">Histórico de Envios</h4>
                    {userDetail.history?.length === 0 ? (
                      <p className="text-sm text-stone-500 text-center py-4">Nenhum envio no período</p>
                    ) : (
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {userDetail.history?.map((item, idx) => (
                          <div key={idx} className="p-3 bg-stone-800 rounded-lg border border-stone-700/50">
                            <p className="text-sm font-medium text-stone-200 truncate">
                              {item.subject || item.template}
                            </p>
                            <p className="text-xs text-stone-500 mb-2">
                              {new Date(item.sent_at).toLocaleString('pt-BR')}
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {item.events?.map((event, eidx) => (
                                <EventBadge key={eidx} type={event.event_type} />
                              ))}
                              {(!item.events || item.events.length === 0) && (
                                <span className="text-xs text-stone-500">Aguardando eventos...</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Totais do usuário */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-stone-800 rounded-lg p-2 border border-stone-700/50">
                      <p className="text-lg font-semibold text-stone-100">{userDetail.totals?.sent || 0}</p>
                      <p className="text-xs text-stone-500">Enviados</p>
                    </div>
                    <div className="bg-green-500/10 rounded-lg p-2 border border-green-500/30">
                      <p className="text-lg font-semibold text-green-400">{userDetail.totals?.opened || 0}</p>
                      <p className="text-xs text-stone-500">Abertos</p>
                    </div>
                    <div className="bg-purple-500/10 rounded-lg p-2 border border-purple-500/30">
                      <p className="text-lg font-semibold text-purple-400">{userDetail.totals?.clicked || 0}</p>
                      <p className="text-xs text-stone-500">Cliques</p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-stone-500 text-center">Erro ao carregar detalhes</p>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
